import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import React, { useContext, useEffect, useState } from "react";
import MDSelectBox from "components/MDSelectBox";
import { useFormik } from "formik";
import { initialValues } from "./Schemas/initialValue";
import { validationSchema } from "./Schemas/validation";
import { ModalContext } from "context/ModalContext";
import PropTypes from "prop-types";
import { LoginFormContext } from "context/LoginFormContext";
import { amenityAdd, updateAmenity } from "Apis/amenity";
import ImageUploader from "components/MDImageUploader";
import SubmitButton from "components/SubmitButton";
import { statusData } from "helpers/constant";
import TinyTextEditor from "components/MDTinyTextEditor";
import SingleCheckbox from "components/MDCheckBox/MDSingleCheckBox";
import { AmenityContext } from "context/AmenityContext";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Divider from "@mui/material/Divider";
import { InputAdornment, Typography } from "@mui/material";

const EMPTY_PRICE_ROW = {
  occasion_id: null,
  member_price: "",
  non_member_price: "",
};

const AminityForm = ({ getAmenityList }) => {
  const { editData, getAllOccasions, occasionList } = useContext(AmenityContext);
  const [isOnlineBooking, setIsOnlineBooking] = useState(0);
  const { setNotification, profileImage, setProfileImage } = useContext(LoginFormContext);
  const { setOpen, formIsEdit } = useContext(ModalContext);
  const [status, setStatus] = useState({ label: "Inactive", value: "0" });

  const occasionOptions = (occasionList || []).map((o) => ({
    label: o.label ?? o.name,
    value: Number(o.id),
  }));

  const getStatusLabel = (id) => {
    const s = statusData.find((item) => item.value == id);
    return s ? s.label : "-";
  };

  const ruleError = (index, field) => {
    const touched = formik?.touched?.amenity_occasion_prices?.[index]?.[field];
    const error = formik?.errors?.amenity_occasion_prices?.[index]?.[field];
    return touched && error ? error : null;
  };

  // ── Row CRUD ──────────────────────────────────────────────────────────────
  const addRow = () => {
    formik?.setFieldValue("amenity_occasion_prices", [
      ...formik?.values.amenity_occasion_prices,
      { ...EMPTY_PRICE_ROW },
    ]);
  };

  const removeRow = (index) => {
    const updated = formik?.values?.amenity_occasion_prices?.filter((_, i) => i !== index);
    formik.setFieldValue("amenity_occasion_prices", updated);
  };

  const handleRowField = (index, field, value) => {
    const updated = formik?.values?.amenity_occasion_prices?.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    );
    formik.setFieldValue("amenity_occasion_prices", updated);
  };

  const setDataInFormik = () => {
    formik.setValues({
      ...editData,
      amenity_occasion_prices: (editData?.amenity_occasion_prices ?? []).map((r) => ({
        occasion_id: r?.occasion_id != null ? Number(r?.occasion_id) : null,
        member_price: r?.member_price ?? "",
        non_member_price: r?.non_member_price ?? "",
      })),
    });
    formik.setFieldValue("amenity_images", editData?.amenity_images?.[0]?.image.split("/").pop());
    setIsOnlineBooking(editData?.is_online_booking_available);
    setProfileImage({ url: editData?.amenity_images?.[0]?.image || null, name: null });
  };

  useEffect(() => {
    const init = async () => {
      await getAllOccasions();
      if (formIsEdit) {
        setDataInFormik();
        setStatus({
          label: getStatusLabel(editData?.is_active),
          value: editData?.is_active,
        });
      } else {
        setProfileImage({ url: null, name: null });
      }
    };
    init();
  }, []);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (values) => {
    const data = {
      ...(formIsEdit ? { id: editData?.id } : {}),
      name: values?.name,
      description: values?.description,
      terms_and_condition: values?.terms_and_condition,
      is_online_booking_available: values?.is_online_booking_available,
      is_active: values?.is_active,
      amenity_images: values?.amenity_images
        ? [values?.amenity_images]
        : formIsEdit && editData
          ? [editData?.amenity_images?.[0]?.image?.split("/").pop()]
          : [],
      amenity_occasion_prices: values?.amenity_occasion_prices
        .filter((r) => r?.occasion_id !== null)
        .map((r) => ({
          occasion_id: Number(r?.occasion_id),
          member_price: Number(r?.member_price),
          non_member_price: Number(r?.non_member_price),
        })),
    };

    if (!formIsEdit) {
      const response = await amenityAdd(data);
      if (response?.status === 200) {
        getAmenityList();
        setOpen(false);
        setNotification({
          color: "success",
          icon: "check",
          title: "Success",
          content: response?.data?.message,
          open: true,
        });
      }
    } else {
      const response = await updateAmenity(data);
      if (response?.status === 200) {
        getAmenityList();
        setOpen(false);
        setNotification({
          color: "success",
          icon: "check",
          title: "Success",
          content: response?.data?.message,
          open: true,
        });
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response?.data?.message,
          open: true,
        });
      }
    }
  };

  const handleIsDefault = (e) => {
    setIsOnlineBooking(e?.target?.checked);
    formik.setFieldValue("is_online_booking_available", e?.target?.checked ? 1 : 0);
  };

  const handleStatusChange = (value) => {
    formik.setFieldValue("is_active", value ? value?.value : null);
    setStatus(value ?? null);
  };

  const formik = useFormik({ initialValues, validationSchema, onSubmit: handleSubmit });

  return (
    <MDBox component="form" role="form" onSubmit={formik?.handleSubmit}>
      <Grid container spacing={2} rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid item xs={12} sx={{ paddingTop: "20px !important" }}>
          <ImageUploader
            img={profileImage?.url}
            folderName="/amenities"
            name="amenity_images"
            fileName="amenity_images"
            formik={formik}
            label="Amenity Image"
            sx={{
              width: "100px",
              height: "100px",
              objectFit: "cover",
              borderRadius: "50%",
              marginBottom: "16px",
            }}
            required
          />
          {formik?.errors?.amenity_images && (
            <MDBox sx={{ fontSize: "12px", color: "red", marginLeft: "5px" }}>
              {formik?.errors?.amenity_images}
            </MDBox>
          )}
        </Grid>

        <Grid item xs={12} sx={{ paddingTop: "30px !important" }}>
          <MDInput
            type="text"
            name="name"
            required
            label="Amenity Name"
            variant="standard"
            value={formik?.values?.name}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            fullWidth
          />
          {formik?.touched?.name && formik?.errors?.name && (
            <MDBox sx={{ fontSize: "12px", color: "red", marginTop: "5px" }}>
              {formik?.errors?.name}
            </MDBox>
          )}
        </Grid>

        {/* ── Description ────────────────────────────────────────────────── */}
        <Grid item xs={12} sx={{ paddingTop: "30px !important" }}>
          <MDBox
            sx={{ fontSize: "16px", fontWeight: "400", marginBottom: "5px", marginTop: "10px" }}
          >
            Description <span style={{ color: "red" }}>*</span>
          </MDBox>
          <TinyTextEditor
            setContent={(v) => formik?.setFieldValue("description", v)}
            content={formik?.values?.description}
            onBlur={formik?.handleBlur}
            folderName="/post"
          />
          {formik?.touched?.description && formik?.errors?.description && (
            <MDBox sx={{ fontSize: "12px", color: "red", marginTop: "5px" }}>
              {formik?.errors?.description}
            </MDBox>
          )}
        </Grid>

        {/* ── Terms ──────────────────────────────────────────────────────── */}
        <Grid item xs={12} sx={{ paddingTop: "30px !important" }}>
          <MDBox
            sx={{ fontSize: "16px", fontWeight: "400", marginBottom: "5px", marginTop: "10px" }}
          >
            Terms and Conditions <span style={{ color: "red" }}>*</span>
          </MDBox>
          <TinyTextEditor
            setContent={(v) => formik?.setFieldValue("terms_and_condition", v)}
            content={formik?.values?.terms_and_condition}
            onBlur={formik?.handleBlur}
            folderName="/post"
          />
          {formik?.touched?.terms_and_condition && formik?.errors?.terms_and_condition && (
            <MDBox sx={{ fontSize: "12px", color: "red", marginTop: "5px" }}>
              {formik?.errors?.terms_and_condition}
            </MDBox>
          )}
        </Grid>

        {/* ── Online Booking ─────────────────────────────────────────────── */}
        <Grid item xs={12} md={6} sx={{ paddingTop: "30px !important" }}>
          <SingleCheckbox
            name="is_online_booking_available"
            label="This amenity is available for online booking"
            onChange={handleIsDefault}
            onBlur={formik?.handleBlur}
            checked={isOnlineBooking}
          />
        </Grid>

        {/* ── Status ─────────────────────────────────────────────────────── */}
        <Grid item xs={12} md={6} sx={{ paddingTop: "30px !important" }}>
          <MDSelectBox
            name="is_active"
            defaultValue={status}
            options={statusData}
            handleChange={handleStatusChange}
            label="Status"
            width="100%"
            multiple={false}
            onBlur={formik?.handleBlur}
            required
          />
          {formik?.touched?.is_active && formik?.errors?.is_active && (
            <MDBox sx={{ fontSize: "12px", color: "red", marginTop: "5px" }}>
              {formik?.errors?.is_active}
            </MDBox>
          )}
        </Grid>

        {/* ── Occasion Pricing ───────────────────────────────────────────── */}
        <Grid item xs={12} sx={{ paddingTop: "30px !important" }}>
          <Divider />
          <MDBox
            sx={{ fontSize: "16px", fontWeight: "500", marginTop: "16px", marginBottom: "12px" }}
          >
            Occasion Pricing
          </MDBox>

          {formik?.values?.amenity_occasion_prices?.map((row, index) => {
            const selectedOccasion =
              occasionOptions?.find((o) => Number(o?.value) === Number(row?.occasion_id)) ?? null;

            const selectedOccasionIds = formik?.values?.amenity_occasion_prices
              .map((r, i) => (i !== index ? Number(r?.occasion_id) : null))
              .filter((id) => id !== null && !isNaN(id));

            const filteredOccasionOptions = occasionOptions
              ?.filter((o) => !selectedOccasionIds?.includes(Number(o?.value)))
              ?.sort((a, b) => a?.label?.localeCompare(b?.label));

            return (
              <MDBox key={index} sx={{ position: "relative", marginBottom: "20px" }}>
                <MDBox
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "16px 16px 12px",
                    marginBottom: "14px",
                    backgroundColor: "#fafafa",
                    position: "relative",
                  }}
                >
                  <Grid container spacing={2}>
                    {/* ── Occasion: Dropdown OR Locked Label ─────────────────── */}
                    <Grid item xs={12}>
                      {!selectedOccasion ? (
                        // ✅ No occasion selected yet → show dropdown
                        <>
                          <MDSelectBox
                            key={`empty-${index}`}
                            name={`amenity_occasion_prices[${index}]?.occasion_id`}
                            defaultValue={null}
                            options={filteredOccasionOptions}
                            handleChange={(opt) =>
                              handleRowField(index, "occasion_id", opt ? Number(opt.value) : null)
                            }
                            label="Select Occasion"
                            width="100%"
                            multiple={false}
                            required
                          />
                          {ruleError(index, "occasion_id") && (
                            <MDBox sx={{ fontSize: "12px", color: "red", marginTop: "4px" }}>
                              {ruleError(index, "occasion_id")}
                            </MDBox>
                          )}
                        </>
                      ) : (
                        // ✅ Occasion selected → show as locked label
                        <MDBox
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "6px 12px",
                            backgroundColor: "#FFF2F2",
                            border: "1px solid #A68181",
                            borderRadius: "6px",
                            width: "fit-content",
                          }}
                        >
                          <MDBox
                            sx={{
                              fontSize: "13px",
                              color: "#555",
                              fontWeight: "400",
                              marginRight: "2px",
                            }}
                          >
                            Occasion:
                          </MDBox>
                          <MDBox
                            sx={{
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#A68181",
                            }}
                          >
                            {selectedOccasion?.label}
                          </MDBox>
                          {/* 🔒 Lock icon to visually indicate it's not editable */}
                          {/* <MDBox
                            component="span"
                            sx={{ fontSize: "13px", color: "#90a4ae", marginLeft: "4px" }}
                            title="Occasion cannot be changed once selected"
                          >
                            🔒
                          </MDBox> */}
                        </MDBox>
                      )}
                    </Grid>

                    {/* ── Price & Deposit fields (always shown after occasion picked) ── */}
                    {selectedOccasion && (
                      <>
                        {/* Member Price */}
                        <Grid item xs={12} sm={6}>
                          <MDInput
                            type="text"
                            label="Member Price"
                            variant="standard"
                            required
                            fullWidth
                            value={row.member_price}
                            onChange={(e) => {
                              const value = e.target.value;

                              // Allow only whole numbers
                              if (/^\d*$/.test(value)) {
                                handleRowField(index, "member_price", value);
                              }
                            }}
                            onBlur={() =>
                              formik.setFieldTouched(
                                `amenity_occasion_prices[${index}].member_price`,
                                true
                              )
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Typography
                                    sx={{ fontSize: "13px", color: "#6B7280", mr: "2px" }}
                                  >
                                    ₹
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                            inputProps={{
                              inputMode: "numeric",
                              pattern: "[0-9]*",
                            }}
                          />
                          {ruleError(index, "member_price") && (
                            <MDBox sx={{ fontSize: "12px", color: "red", marginTop: "4px" }}>
                              {ruleError(index, "member_price")}
                            </MDBox>
                          )}
                        </Grid>
                        {/* Non-Member Price */}
                        <Grid item xs={12} sm={6}>
                          <MDInput
                            type="text"
                            label="Non-Member Price"
                            variant="standard"
                            required
                            fullWidth
                            value={row?.non_member_price}
                            onChange={(e) => {
                              const value = e.target.value;

                              // Allow only whole numbers
                              if (/^\d*$/.test(value)) {
                                handleRowField(index, "non_member_price", value);
                              }
                            }}
                            onBlur={() =>
                              formik?.setFieldTouched(
                                `amenity_occasion_prices[${index}].non_member_price`,
                                true
                              )
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Typography
                                    sx={{ fontSize: "13px", color: "#6B7280", mr: "2px" }}
                                  >
                                    ₹
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                            inputProps={{
                              inputMode: "numeric",
                              pattern: "[0-9]*",
                            }}
                          />
                          {ruleError(index, "non_member_price") && (
                            <MDBox sx={{ fontSize: "12px", color: "red", marginTop: "4px" }}>
                              {ruleError(index, "non_member_price")}
                            </MDBox>
                          )}
                        </Grid>
                      </>
                    )}
                  </Grid>
                </MDBox>

                {/* Delete row button */}
                <IconButton
                  onClick={() => removeRow(index)}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    color: "#e53935",
                    "&:hover": { backgroundColor: "#ffebee" },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </MDBox>
            );
          })}

          <MDBox
            onClick={addRow}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
              color: "#A68181",
              fontSize: "14px",
              fontWeight: "500",
              padding: "6px 12px",
              border: "1px dashed #A68181",
              borderRadius: "6px",
              // "&:hover": { backgroundColor: "#A68181" },
              marginTop: "4px",
            }}
          >
            <AddIcon fontSize="small" />
            Add Occasion Pricing
          </MDBox>
        </Grid>
      </Grid>

      <MDBox sx={{ width: "100%", textAlign: "center", marginTop: "40px" }}>
        <SubmitButton btnText={formIsEdit ? "Update" : "Add"} type="submit" />
      </MDBox>
    </MDBox>
  );
};

AminityForm.propTypes = {
  getAmenityList: PropTypes.func,
};

export default AminityForm;
